import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { LoaderCircle, SearchX, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { initials } from '@/lib/format';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <span className="spinner" role="status"><LoaderCircle aria-hidden /> <span>{label}</span></span>;
}

export function LoadingState({ label = 'Gathering the latest…' }: { label?: string }) {
  return <div className="state-card"><Spinner label={label} /></div>;
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong while loading this page.';
  return (
    <div className="state-card state-error" role="alert">
      <span className="state-icon"><SearchX aria-hidden /></span>
      <div><strong>We couldn’t load this yet.</strong><p>{message}</p></div>
      {onRetry && <button className="button secondary" type="button" onClick={onRetry}>Try again</button>}
    </div>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return <div className="state-card"><span className="state-icon"><SearchX aria-hidden /></span><div><strong>{title}</strong><p>{body}</p></div>{action}</div>;
}

export function Avatar({ name, src, size = 'md' }: { name: string; src?: string | null; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  return src ? <img className={`avatar avatar-${size}`} src={src} alt="" loading="lazy" /> : <span className={`avatar avatar-${size} avatar-fallback`} aria-hidden>{initials(name)}</span>;
}

export function Verified({ label = 'Verified' }: { label?: string }) {
  return <span className="verified"><ShieldCheck aria-hidden />{label}</span>;
}

export function Tags({ values, limit = 4 }: { values?: Array<string | { name: string }>; limit?: number }) {
  if (!values?.length) return null;
  return <div className="tags">{values.slice(0, limit).map((value) => { const label = typeof value === 'string' ? value : value.name; return <span className="tag" key={label}>{label}</span>; })}</div>;
}

export function SubmitButton({ pending, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { pending?: boolean }) {
  return <button {...props} disabled={pending || props.disabled} className={`button primary ${props.className ?? ''}`}>{pending ? <Spinner label="Working…" /> : children}</button>;
}

export function PageIntro({ eyebrow, title, body, action }: { eyebrow?: string; title: string; body: string; action?: ReactNode }) {
  return <header className="page-intro">{eyebrow && <span className="eyebrow">{eyebrow}</span>}<div className="page-intro-row"><div><h1>{title}</h1><p>{body}</p></div>{action}</div></header>;
}

export function Crumb({ to, children }: { to: string; children: ReactNode }) {
  return <Link className="crumb" to={to}>← {children}</Link>;
}
